// Under Step 3's contract milestones, folded behind a question: where the projected COD
// comes from, explaining only the date the page shows, the page's own plan or the
// specialists' achievable COD where that is later (schedule.projectCod), read against the
// PPA COD (docs/Y2026-Q4/design-amazon-overview.md).
import { useState } from "react";

import type { AmazonBuilding } from "../../api/hooks/useAmazonOverview";
import { useCitedTexts } from "../../components/useReadinessCitations";
import { Chevron } from "../../components/Chevron";
import { day, daysBetween, formatDate } from "./dates";
import type { Target } from "./editContext";
import { Editable } from "./editing";
import { buildingKey } from "./overrides";
import { codLine, recordedBy } from "./codLine";
import { type Projection, type Scheduled } from "./schedule";
import styles from "./amazonOverview.module.css";

export const CodBasis = ({
  b,
  schedule,
  projection,
  plan: planTarget,
}: {
  b: AmazonBuilding;
  schedule: Record<string, Scheduled>;
  projection: Projection;
  /** What the plan's date is worked out from, for the edit pane. */
  plan: Target;
}) => {
  const [open, setOpen] = useState(false);
  const basis = b.achievable_cod_basis ?? null;
  const { cite, modal } = useCitedTexts(basis?.reason ? [basis.reason] : []);
  const ppa = b.committed_cod ? day(b.committed_cod) : null;
  const { date, bySpecialists } = projection;
  const late = ppa && date ? daysBetween(ppa, date) : null;
  const against =
    late == null
      ? ""
      : late > 0
        ? `, ${late} days after the PPA COD of ${formatDate(ppa)}`
        : `, on time for the PPA COD of ${formatDate(ppa)}`;
  const shown = date ? (
    <Editable
      target={
        bySpecialists
          ? { kind: "building", projectId: b.project_id }
          : planTarget
      }
      keys={bySpecialists ? [buildingKey("achievable_cod"), buildingKey("cod_reason")] : []}
    >
      {formatDate(date)}
    </Editable>
  ) : null;
  return (
    <div className={styles.codBasis}>
      <button
        type="button"
        className={styles.askToggle}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        How is the projected COD worked out? <Chevron open={open} />
      </button>
      {open &&
        (!date ? (
          <p>
            No achievable size is recorded, so installation cannot be timed, and no specialist
            has recorded an achievable COD.
          </p>
        ) : bySpecialists ? (
          <>
            <p>
              The projected COD is <b>{shown}</b>
              {against}: the specialists' achievable COD, the earliest date {recordedBy(b)}{" "}
              as possible.
            </p>
            {basis?.reason && <p>Their reason: {cite(basis.reason)}</p>}
          </>
        ) : (
          <>
            <p>
              The projected COD is <b>{shown}</b>
              {against}, worked out by the page from tomorrow, step by step.
            </p>
            <p>{codLine(b, schedule, projection)}</p>
            <p>The plan assumes:</p>
            <ul>
              <li>Steps already done keep the dates on record.</li>
              <li>
                Every open step is finished as soon as possible from tomorrow, in Step 1's order,
                each taking Step 1's duration.
              </li>
              <li>
                The grid offer comes on the DNO's date, or 3 months after we applied if the DNO
                gave none.
              </li>
              <li>
                Amazon's design approval (DR3) takes the full 104 Business Days: weekdays, less
                bank holidays in England and Wales.
              </li>
              <li>
                Nothing that interrupts the power (the grid connection, commissioning, the
                switchgear discovery shutdown) is planned from 1 October to 15 January, and
                cranes are off site by the end of 14 October (MSPPA s5.8). Installation itself
                carries on through the winter.
              </li>
            </ul>
          </>
        ))}
      {modal}
    </div>
  );
};
